import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import api from './api'
import { store } from './store'

/**
 * SECURITY FIX: Admin API Guard with Method-Based Protection
 * 
 * This guard now uses BOTH URL path AND HTTP method to determine admin access requirements:
 * 
 * REGULAR USER ACCESS (No admin headers):
 * - GET /users (for task assignments, user selection, etc.)
 * 
 * ADMIN ACCESS REQUIRED (Admin headers added):
 * - POST /users (create users)
 * - PUT/PATCH /users/:id (edit users) 
 * - DELETE /users/:id (delete users)
 * - All /roles, /permissions, /user_roles operations
 * 
 * This prevents the security loophole where regular user operations
 * were being treated as admin operations.
 */

// Admin API operations that require special protection
const ADMIN_OPERATIONS = [
  // User Management (admin only)
  { path: '/users', methods: ['POST', 'PUT', 'PATCH', 'DELETE'] },
  
  // Roles Management (admin only - all methods)
  { path: '/roles', methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] },
  
  // Permissions Management (admin only - all methods) 
  { path: '/permissions', methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] },
  
  // User Role Assignments (admin only - all methods)
  { path: '/user_roles', methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] }
]

// Super Admin operations that require super admin privileges
const SUPER_ADMIN_OPERATIONS = [
  // Tenants Management (super admin only)
  { path: '/tenants', methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] },
  
  // Super Admin User Management (super admin only)
  { path: '/super_admin/users', methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] },
  
  // Super Admin specific endpoints
  { path: '/super_admin/', methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] }
]

// Check if endpoint requires admin access based on path AND method
const isAdminEndpoint = (url: string, method: string = 'GET'): boolean => {
  return ADMIN_OPERATIONS.some(operation => {
    const matchesPath = url.includes(operation.path)
    const matchesMethod = operation.methods.includes(method.toUpperCase())
    return matchesPath && matchesMethod
  })
}

// Check if endpoint requires super admin access
const isSuperAdminEndpoint = (url: string, method: string = 'GET'): boolean => {
  return SUPER_ADMIN_OPERATIONS.some(operation => {
    const matchesPath = url.includes(operation.path)
    const matchesMethod = operation.methods.includes(method.toUpperCase())
    return matchesPath && matchesMethod
  })
}

// Check if user has admin access based on Redux state
const hasAdminAccess = (): { hasAccess: boolean; reason?: string } => {
  const state = store.getState()
  const { user, permissions, isAuthenticated } = state.auth

  if (!isAuthenticated) {
    return { hasAccess: false, reason: 'Not authenticated' }
  }

  if (!user) {
    return { hasAccess: false, reason: 'No user data' }
  }

  if (!user.active) {
    return { hasAccess: false, reason: 'User account inactive' }
  }

  if (!user.email_verified) {
    return { hasAccess: false, reason: 'Email not verified' }
  }

  // Check admin role
  const hasAdminRole = user.roles && user.roles.some((role: any) => 
    role.name === 'admin' || role.name === 'Admin'
  )

  // Check admin permissions
  const hasAdminPermissions = permissions && permissions.some((permission: string) => 
    permission.includes('admin:') || 
    permission.includes('users:create') || 
    permission.includes('users:delete') ||
    permission.includes('roles:create') ||
    permission.includes('permissions:create')
  )

  if (!hasAdminRole && !hasAdminPermissions) {
    return { hasAccess: false, reason: 'Insufficient admin privileges' }
  }

  return { hasAccess: true }
}

// Check if user has super admin access
const hasSuperAdminAccess = (): { hasAccess: boolean; reason?: string } => {
  const state = store.getState()
  const { user, isAuthenticated } = state.auth

  if (!isAuthenticated) {
    return { hasAccess: false, reason: 'Not authenticated' }
  }

  if (!user) {
    return { hasAccess: false, reason: 'No user data' }
  }

  if (!user.active) {
    return { hasAccess: false, reason: 'User account inactive' }
  }

  // Check if user is super admin
  if (!user.is_super_admin) {
    return { hasAccess: false, reason: 'Super admin privileges required' }
  }

  return { hasAccess: true }
}

// Enhanced admin security headers
const getAdminSecurityHeaders = () => {
  const state = store.getState()
  const { user } = state.auth
  
  return {
    'X-Admin-User-Id': user?.id?.toString() || '',
    'X-Admin-Email': user?.email || '',
    'X-Admin-Timestamp': new Date().toISOString(),
    'X-Admin-Client': 'web-admin-panel'
  }
}

// Request interceptor for admin API security
const adminRequestInterceptor = (config: any): any => {
  const url = config.url || ''
  const method = config.method || 'GET'
  
  // Check if this is a super admin endpoint
  if (isSuperAdminEndpoint(url, method)) {
    
    // Verify super admin access
    const { hasAccess, reason } = hasSuperAdminAccess()
    
    if (!hasAccess) {
      const error = new Error(`Super Admin API Access Denied: ${reason}`)
      error.name = 'SuperAdminAccessDenied'
      console.error('🚫 Super Admin API Request Blocked:', {
        url,
        method: config.method,
        reason,
        timestamp: new Date().toISOString()
      })
      throw error
    }

    // Add super admin security headers
    const adminHeaders = getAdminSecurityHeaders()
    config.headers = {
      ...config.headers,
      ...adminHeaders,
      'X-Super-Admin': 'true'
    }

  }
  // Check if this is a regular admin endpoint
  else if (isAdminEndpoint(url, method)) {
    
    // Verify admin access
    const { hasAccess, reason } = hasAdminAccess()
    
    if (!hasAccess) {
      const error = new Error(`Admin API Access Denied: ${reason}`)
      error.name = 'AdminAccessDenied'
      console.error('🚫 Admin API Request Blocked:', {
        url,
        method: config.method,
        reason,
        timestamp: new Date().toISOString()
      })
      throw error
    }

    // Add admin security headers
    const adminHeaders = getAdminSecurityHeaders()
    config.headers = {
      ...config.headers,
      ...adminHeaders
    }

  }

  return config
}

// Response interceptor for admin API security
const adminResponseInterceptor = (response: AxiosResponse): AxiosResponse => {
  const url = response.config.url || ''
  const method = response.config.method || 'GET'
  
  if (isAdminEndpoint(url, method)) {
    
    // Log admin API usage for audit
    const auditLog = {
      type: 'ADMIN_API_SUCCESS',
      url,
      method: response.config.method,
      status: response.status,
      timestamp: new Date().toISOString(),
      user_id: response.config.headers?.['X-Admin-User-Id']
    }
    
  }

  return response
}

// Error interceptor for admin API security
const adminErrorInterceptor = (error: AxiosError): Promise<AxiosError> => {
  const url = error.config?.url || ''
  const method = error.config?.method || 'GET'
  
  if (isSuperAdminEndpoint(url, method)) {
    console.error(`❌ Super Admin API Error: ${error.response?.status} ${url}`)
    
    // Enhanced error logging for super admin endpoints
    const securityLog = {
      type: 'SUPER_ADMIN_API_ERROR',
      url,
      method: error.config?.method,
      status: error.response?.status,
      error_message: error.message,
      timestamp: new Date().toISOString(),
      user_id: error.config?.headers?.['X-Admin-User-Id']
    }
    
    console.error('🚨 Super Admin API Security Log:', securityLog)
    
    // Handle specific super admin access errors
    if (error.response?.status === 403) {
      const customError = new Error('Super admin access denied by server')
      customError.name = 'SuperAdminAccessDeniedServer'
      return Promise.reject(customError)
    }
    
    if (error.response?.status === 401) {
      const customError = new Error('Super admin authentication failed')
      customError.name = 'SuperAdminAuthenticationFailed'
      return Promise.reject(customError)
    }
  }
  else if (isAdminEndpoint(url, method)) {
    console.error(`❌ Admin API Error: ${error.response?.status} ${url}`)
    
    // Enhanced error logging for admin endpoints
    const securityLog = {
      type: 'ADMIN_API_ERROR',
      url,
      method: error.config?.method,
      status: error.response?.status,
      error_message: error.message,
      timestamp: new Date().toISOString(),
      user_id: error.config?.headers?.['X-Admin-User-Id']
    }
    
    console.error('🚨 Admin API Security Log:', securityLog)
    
    // Handle specific admin access errors
    if (error.response?.status === 403) {
      const customError = new Error('Admin access denied by server')
      customError.name = 'AdminAccessDeniedServer'
      return Promise.reject(customError)
    }
    
    if (error.response?.status === 401) {
      const customError = new Error('Admin authentication failed')
      customError.name = 'AdminAuthenticationFailed'
      return Promise.reject(customError)
    }
  }

  return Promise.reject(error)
}

// Initialize admin API security
export const initializeAdminApiSecurity = () => {
  // Add request interceptor
  api.interceptors.request.use(
    adminRequestInterceptor,
    (error) => {
      console.error('Admin API Request Interceptor Error:', error)
      return Promise.reject(error)
    }
  )

  // Add response interceptor
  api.interceptors.response.use(
    adminResponseInterceptor,
    adminErrorInterceptor
  )

}

// Utility functions for admin API security
export const AdminApiSecurity = {
  /**
   * Check if user can access admin APIs
   */
  canAccessAdminApis: () => hasAdminAccess(),
  
  /**
   * Check if user can access super admin APIs
   */
  canAccessSuperAdminApis: () => hasSuperAdminAccess(),
  
  /**
   * Manually verify admin access before making API calls
   */
  verifyAdminAccess: () => {
    const { hasAccess, reason } = hasAdminAccess()
    if (!hasAccess) {
      throw new Error(`Admin access verification failed: ${reason}`)
    }
    return true
  },
  
  /**
   * Manually verify super admin access before making API calls
   */
  verifySuperAdminAccess: () => {
    const { hasAccess, reason } = hasSuperAdminAccess()
    if (!hasAccess) {
      throw new Error(`Super admin access verification failed: ${reason}`)
    }
    return true
  },

  /**
   * Get admin security context for logging
   */
  getSecurityContext: () => {
    const state = store.getState()
    const { user } = state.auth
    
    return {
      user_id: user?.id,
      user_email: user?.email,
      has_admin_access: hasAdminAccess().hasAccess,
      has_super_admin_access: hasSuperAdminAccess().hasAccess,
      is_super_admin: user?.is_super_admin || false,
      timestamp: new Date().toISOString()
    }
  },

  /**
   * Log admin action for audit trail
   */
  auditAdminAction: (action: string, details: any = {}) => {
    const auditEntry = {
      type: 'ADMIN_ACTION',
      action,
      details,
      security_context: AdminApiSecurity.getSecurityContext(),
      timestamp: new Date().toISOString()
    }
    
    
    // In production, send to audit service
    // await auditService.log(auditEntry)
  }
}

// Export the endpoint checkers for other modules
export { isAdminEndpoint, isSuperAdminEndpoint }
